import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import {isReactNativeWebView, postPdfToReactNative} from './reactNativePdfBridge'

export type DigitalVosPdfResult = {
  fileName: string
  base64: string
  blob: Blob
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  const chunkSize = 0x8000
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize)
    binary += String.fromCharCode.apply(null, Array.from(chunk))
  }
  return btoa(binary)
}

export async function buildDigitalVosPdf(
  rootElement: HTMLElement,
  fileName: string
): Promise<DigitalVosPdfResult> {
  const pages = rootElement.querySelectorAll<HTMLElement>('.form-page, .page')
  if (!pages.length) {
    throw new Error('No form pages to export')
  }

  const pdf = new jsPDF('p', 'mm', 'a4')
  const imgWidth = 210
  const pageHeight = 297

  for (let i = 0; i < pages.length; i++) {
    const pageEl = pages[i]
    const canvas = await html2canvas(pageEl, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
    })

    const imgData = canvas.toDataURL('image/png')
    const imgHeight = (canvas.height * imgWidth) / canvas.width

    if (i > 0) {
      pdf.addPage()
    }

    let heightLeft = imgHeight
    let position = 0

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
    heightLeft -= pageHeight

    while (heightLeft > 0) {
      position = heightLeft - imgHeight
      pdf.addPage()
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
      heightLeft -= pageHeight
    }
  }

  const normalizedName = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`
  const pdfBytes = pdf.output('arraybuffer')
  const base64 = arrayBufferToBase64(pdfBytes)
  const blob = new Blob([pdfBytes], {type: 'application/pdf'})

  return {fileName: normalizedName, base64, blob}
}

export async function exportDigitalVosPdf(
  rootElement: HTMLElement,
  fileName: string
): Promise<void> {
  const result = await buildDigitalVosPdf(rootElement, fileName)

  if (isReactNativeWebView()) {
    postPdfToReactNative(result.fileName, result.base64)
    return
  }

  const url = URL.createObjectURL(result.blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = result.fileName
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}

export async function waitForImages(container: HTMLElement, timeoutMs = 15000): Promise<void> {
  const images = Array.from(container.querySelectorAll('img'))
  if (!images.length) return

  await Promise.race([
    Promise.all(
      images.map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete && img.naturalHeight > 0) {
              resolve()
              return
            }
            img.onload = () => resolve()
            img.onerror = () => resolve()
          })
      )
    ),
    new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
  ])
}
